import {useEffect, useCallback, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import storage from '../utils/storage';
import logger from '../utils/logger';

/**
 * Composant pour initialiser la sécurité multi-onglets
 * 
 * Fonctionnalités:
 * - Vérifie la cohérence du token au démarrage
 * - Synchronise la déconnexion entre les onglets
 * - Synchronise la connexion entre les onglets
 * - Synchronise l'inactivité entre les onglets
 * - Force la déconnexion en cas d'incohérence
 */
export default function SecurityInitializer({ children }) {
  const navigate = useNavigate();
  const logoutInProgressRef = useRef(false);

  // Fonction pour gérer la déconnexion
  const handleLogout = useCallback(() => {
    // Éviter les appels multiples
    if (logoutInProgressRef.current) {
      logger.log('[Security] Déconnexion déjà en cours, ignorer');
      return;
    }

    logoutInProgressRef.current = true;
    logger.log('[Security] Déconnexion détectée');
    
    // Ne pas appeler clearAll() ici car déjà fait par l'onglet qui a initié la déconnexion
    // Juste rediriger et afficher le message
    navigate('/');
    toast.info('Vous avez été déconnecté.');

    // Réinitialiser après un délai
    setTimeout(() => {
      logoutInProgressRef.current = false;
    }, 1000);
  }, [navigate]);

  // Fonction pour gérer la connexion
  const handleLogin = useCallback(() => {
    logger.log('[Security] Connexion détectée dans un autre onglet');
    
    // Vérifier si on a maintenant un token valide
    const token = storage.getToken();
    if (token) {
      // Si on a un token, recharger la page pour synchroniser l'état
      logger.log('[Security] Token détecté, rechargement de la page...');
      window.location.reload();
    } else {
      // Si pas de token, c'est peut-être une déconnexion, ne rien faire
      logger.log('[Security] Pas de token détecté, ignorer l\'événement');
    }
  }, []);

  // Fonction pour gérer l'inactivité
  const handleInactivity = useCallback(() => {
    logger.warn('[Security] Inactivité détectée dans tous les onglets');
    storage.clearAll(); // Nettoyer et envoyer le signal de déconnexion
    navigate('/');
    toast.warning('Vous avez été déconnecté en raison de l\'inactivité.');
  }, [navigate]);

  useEffect(() => {
    // Vérification initiale de cohérence
    const initialCheck = () => {
      logger.log('[Security] Vérification initiale de cohérence...');
      const coherence = storage.verifyCoherence(true); // autoClean = true au démarrage

      if (!coherence.isCoherent) {
        logger.warn('[Security] Incohérence détectée au démarrage:', coherence.error);
        // Ne pas afficher de toast ni rediriger si on est déjà sur la page de connexion
        const currentPath = window.location.pathname;
        if (currentPath !== '/' && currentPath !== '/connexion') {
          toast.error('Erreur de sécurité: ' + coherence.error);
          navigate('/');
        }
        return false;
      }

      logger.log('[Security] Vérification initiale OK');
      return true;
    };

    // Listener pour les changements de token dans d'autres onglets
    const handleStorageChange = (event) => {
      const eventKeys = storage.getEventKeys();

      // ✅ Gestion de la déconnexion
      if (event.key === eventKeys.LOGOUT_EVENT_KEY) {
        logger.log('[Security] Événement de déconnexion détecté');
        handleLogout();
        return;
      }

      // ✅ Gestion de la connexion
      if (event.key === eventKeys.LOGIN_EVENT_KEY) {
        logger.log('[Security] Événement de connexion détecté');
        handleLogin();
        return;
      }

      // ✅ Gestion de l'inactivité
      if (event.key === eventKeys.ACTIVITY_EVENT_KEY) {
        logger.log('[Security] Événement d\'activité détecté');
        // Vérifier si l'utilisateur est inactif depuis plus de 1h
        const lastActivity = storage.getLastActivityTime();
        const now = Date.now();
        const inactivityDuration = (now - lastActivity) / (1000 * 60); // en minutes

        if (inactivityDuration > 60) {
          logger.warn('[Security] Inactivité de plus de 1h détectée');
          handleInactivity();
        }
        return;
      }

      // Vérifier les changements de token/rôle/utilisateur
      if (event.key === 'easySurvey.token' || event.key === 'easySurvey.role' || event.key === 'easySurvey.user') {
        logger.log('[Security] Changement de token/rôle/utilisateur détecté');

        // Si le token a été supprimé (déconnexion), ne rien faire car géré par LOGOUT_EVENT_KEY
        if (!event.newValue && event.oldValue) {
          logger.log('[Security] Token supprimé, déconnexion déjà gérée');
          return;
        }

        // Si le token a été ajouté (connexion), ne rien faire car géré par LOGIN_EVENT_KEY
        if (event.newValue && !event.oldValue) {
          logger.log('[Security] Token ajouté, connexion déjà gérée');
          return;
        }

        // Vérifier la cohérence après le changement
        const coherence = storage.verifyCoherence(false); // autoClean = false pour éviter les faux positifs

        if (!coherence.isCoherent) {
          logger.log('[Security] Incohérence détectée après changement:', coherence.error);
          toast.error('Erreur de sécurité: ' + coherence.error);
          storage.clearAll(); // Nettoyer et envoyer le signal de déconnexion
          navigate('/');
          return;
        }
      }
    };

    // Vérification initiale
    const isCoherent = initialCheck();
    
    // Si cohérent, ajouter le listener
    if (isCoherent) {
      window.addEventListener('storage', handleStorageChange);
      logger.log('[Security] Listener multi-onglets activé');
    }

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      logger.log('[Security] Listener multi-onglets désactivé');
    };
  }, [navigate, handleLogout, handleLogin, handleInactivity]);

  return children;
}
